// ---------------------------------------------------------------------------
// Pure helpers for building the storage collection create/update payload.
//
// The backend (CollectionsService.create/update) expects:
//   { name, sources: [{ sourceId, isDefault? }], defaultSourceId, settings }
// where `sources` is an array of objects (NOT a flat `sourceIds: string[]`).
// This module centralises the mapping so it can be tested against the real
// backend contract.
// ---------------------------------------------------------------------------

export interface CollectionFormData {
  name: string;
  description?: string;
  sourceIds: string[];
  defaultSourceId: string;
  versioningEnabled: boolean;
  softDeleteDays: number;
  maxConcurrentJobs: number;
  maxFileSizeMB: number;
  maxTotalSizeMB: number;
}

export interface StorageCollectionSettings {
  versioningEnabled: boolean;
  softDeleteDays: number;
  maxConcurrentJobs: number;
  maxFileSizeBytes?: number;
  maxTotalSizeBytes?: number;
}

export interface StorageCollectionPayload {
  name: string;
  description?: string;
  sources: { sourceId: string; isDefault: boolean }[];
  defaultSourceId: string;
  settings: StorageCollectionSettings;
}

const MB = 1024 * 1024;

/**
 * Build the backend collection payload from the flat UI form data.
 *
 * Converts `sourceIds: string[]` into the nested `sources: [{sourceId,
 * isDefault}]` array that CollectionsService expects, marking the selected
 * default source with `isDefault: true`. Optional size limits are converted
 * from MB to bytes only when > 0 (otherwise omitted).
 */
export function buildStorageCollectionPayload(
  formData: CollectionFormData,
): StorageCollectionPayload {
  return {
    name: formData.name,
    ...(formData.description?.trim() ? { description: formData.description.trim() } : {}),
    sources: formData.sourceIds.map((sourceId) => ({
      sourceId,
      isDefault: sourceId === formData.defaultSourceId,
    })),
    defaultSourceId: formData.defaultSourceId,
    settings: {
      versioningEnabled: formData.versioningEnabled,
      softDeleteDays: formData.softDeleteDays,
      maxConcurrentJobs: formData.maxConcurrentJobs,
      maxFileSizeBytes:
        formData.maxFileSizeMB > 0 ? formData.maxFileSizeMB * MB : undefined,
      maxTotalSizeBytes:
        formData.maxTotalSizeMB > 0 ? formData.maxTotalSizeMB * MB : undefined,
    },
  };
}